import { useEffect, useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPin } from 'lucide-react';
import { MapMarkerPopup } from './MapMarkerPopup';
import { useCategoryHelpers } from '@/hooks/useCategoryHelpers';

interface MapViewProps {
  professionals: Array<{
    id: string;
    business_name?: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    slug: string;
    categories?: string[];
    category?: string;
    address_city?: string;
    address_region?: string;
    profile_image?: string;
    logo_url?: string;
    is_verified?: boolean;
    latitude?: number;
    longitude?: number;
    type?: 'company' | 'professional';
    is_company?: boolean;
    average_rating?: number;
    review_count?: number;
    languages_spoken?: string[];
    subscription_type?: 'base' | 'pro' | 'plus' | null;
    tagline_it?: string;
    tagline_en?: string;
    tagline_es?: string;
  }>;
}

function calculateCenter(professionals: any[]): [number, number] | null {
  const validPros = professionals.filter(p => p.latitude && p.longitude);
  
  if (validPros.length === 0) return null;
  
  const sumLat = validPros.reduce((sum, p) => sum + p.latitude, 0);
  const sumLng = validPros.reduce((sum, p) => sum + p.longitude, 0);
  
  return [sumLng / validPros.length, sumLat / validPros.length];
}

export const MapView = ({ professionals }: MapViewProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);
  const popupRoots = useRef<Array<{ root: any; container: HTMLDivElement }>>([]);
  const [isMounted, setIsMounted] = useState(false);
  
  const { getCategoryNames, isReady: categoriesReady } = useCategoryHelpers();

  const currentLanguage = (i18n.language || 'it').substring(0, 2) as 'it' | 'en' | 'es';
  const translations = {
    viewProfile: t('professionals.viewProfile'),
    viewCompany: t('professionals.viewCompany'),
    companyBadge: t('professionals.companyBadge'),
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const validPros = professionals.filter(p => p.latitude && p.longitude);
  const center = calculateCenter(validPros) || [12.5674, 41.8719]; // Rome fallback
  
  useEffect(() => {
    if (!isMounted || !mapContainer.current || validPros.length === 0 || !categoriesReady) return;

    // Initialize map
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
          }
        },
        layers: [{
          id: 'osm',
          type: 'raster',
          source: 'osm',
          minzoom: 0,
          maxzoom: 19
        }]
      },
      center: center,
      zoom: validPros.length === 1 ? 13 : 6
    });

    // Add markers
    validPros.forEach(pro => {
      const el = document.createElement('div');
      el.className = 'custom-marker';
      el.style.cursor = 'pointer';
      el.innerHTML = `
        <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.164 0 0 7.164 0 16c0 12 16 24 16 24s16-12 16-24c0-8.836-7.164-16-16-16z" fill="hsl(var(--primary))"/>
          <circle cx="16" cy="16" r="6" fill="white"/>
        </svg>
      `;

      // Create popup container for React
      const popupContainer = document.createElement('div');
      const root = createRoot(popupContainer);
      
      // Prepare professional data for popup
      const categoryIds = pro.categories || (pro.category ? [pro.category] : []);
      const categoryNames = getCategoryNames(categoryIds, currentLanguage);
      
      const popupData = {
        id: pro.id,
        slug: pro.slug,
        business_name: pro.business_name || pro.name,
        first_name: pro.first_name || null,
        last_name: pro.last_name || null,
        address_city: pro.address_city,
        address_region: pro.address_region,
        profile_image: pro.profile_image || pro.logo_url,
        is_verified: pro.is_verified,
        is_company: pro.is_company === true,
        average_rating: pro.average_rating || 0,
        review_count: pro.review_count || 0,
        languages_spoken: pro.languages_spoken || [],
        subscription_type: pro.subscription_type,
        tagline_it: pro.tagline_it,
        tagline_en: pro.tagline_en,
        tagline_es: pro.tagline_es,
      };
      
      root.render(
        <MapMarkerPopup 
          professional={popupData}
          categoryNames={categoryNames}
          onNavigate={(path) => navigate(path)}
          currentLanguage={currentLanguage}
          translations={translations}
        />
      );
      popupRoots.current.push({ root, container: popupContainer });

      const popup = new maplibregl.Popup({ 
        offset: 25,
        maxWidth: '320px',
        closeButton: true,
        closeOnClick: false
      })
        .setDOMContent(popupContainer);

      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([pro.longitude!, pro.latitude!])
        .setPopup(popup)
        .addTo(map.current!);

      markers.current.push(marker);
    });

    // Cleanup
    return () => {
      markers.current.forEach(marker => marker.remove());
      markers.current = [];
      popupRoots.current.forEach(({ root }) => {
        setTimeout(() => root.unmount(), 0);
      });
      popupRoots.current = [];
      map.current?.remove();
    };
  }, [isMounted, validPros, center, categoriesReady, getCategoryNames, currentLanguage]);

  if (validPros.length === 0) {
    const totalCount = professionals.length;
    const withoutCoords = professionals.filter(p => !p.latitude || !p.longitude).length;
    
    return (
      <div className="h-[calc(100vh-100px)] w-full flex items-center justify-center bg-muted">
        <div className="text-center p-8 max-w-md">
          <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="font-heading font-bold text-lg uppercase mb-2">
            Mappa non disponibile
          </p>
          <p className="font-body text-sm text-muted-foreground mb-3">
            {totalCount > 0 ? (
              <>
                {withoutCoords} professionista/i su {totalCount} non hanno coordinate impostate.
              </>
            ) : (
              'Nessun professionista trovato nella ricerca.'
            )}
          </p>
          <p className="font-body text-xs text-muted-foreground">
            💡 I professionisti devono compilare città e paese nel loro profilo per apparire sulla mappa.
          </p>
        </div>
      </div>
    );
  }

  if (!isMounted) {
    return (
      <div className="h-[calc(100vh-100px)] w-full flex items-center justify-center bg-muted">
        <div className="text-center p-8">
          <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground animate-pulse" />
          <p className="font-heading font-bold text-lg uppercase">
            Caricamento mappa...
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      ref={mapContainer} 
      className="h-[calc(100vh-100px)] w-full"
    />
  );
};
